import React, { useState, useEffect, useRef } from 'react';

const CurrencyExchange = () => {
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cadAmount, setCadAmount] = useState('');
  const [usdAmount, setUsdAmount] = useState('');
  const exchangeRateCacheRef = useRef({ rate: null, timestamp: null });

  // Fetch CAD to USD exchange rate from Bank of Canada
  const fetchExchangeRate = async () => {
    const now = Date.now();
    const cacheAge = 60 * 60 * 1000; // 1 hour
    
    if (exchangeRateCacheRef.current.rate && 
        exchangeRateCacheRef.current.timestamp && 
        (now - exchangeRateCacheRef.current.timestamp) < cacheAge) {
      setExchangeRate(exchangeRateCacheRef.current.rate);
      setLastUpdated(new Date(exchangeRateCacheRef.current.timestamp));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://www.bankofcanada.ca/valet/observations/FXUSDCAD/json?recent=1');
      const data = await response.json();
      
      if (data.observations && data.observations.length > 0) {
        const latestObservation = data.observations[0];
        const rate = parseFloat(latestObservation.FXUSDCAD.v);
        const cadToUsdRate = 1 / rate;
        
        exchangeRateCacheRef.current = { rate: cadToUsdRate, timestamp: now };
        window.cadToUsdRate = cadToUsdRate;
        setExchangeRate(cadToUsdRate);
        setLastUpdated(new Date(now));
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      if (exchangeRateCacheRef.current.rate) {
        setExchangeRate(exchangeRateCacheRef.current.rate);
        setLastUpdated(new Date(exchangeRateCacheRef.current.timestamp));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  const handleCadChange = (value) => {
    setCadAmount(value);
    if (value && exchangeRate) {
      setUsdAmount((parseFloat(value) * exchangeRate).toFixed(2));
    } else {
      setUsdAmount('');
    }
  };

  const handleUsdChange = (value) => {
    setUsdAmount(value);
    if (value && exchangeRate) {
      setCadAmount((parseFloat(value) / exchangeRate).toFixed(2));
    } else {
      setCadAmount('');
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ 
        fontSize: '1.8rem', 
        fontWeight: '700', 
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        💱 Currency Exchange
      </h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Live CAD to USD exchange rate from Bank of Canada
      </p>

      {/* Exchange Rate Card */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
            Loading exchange rate...
          </div>
        ) : exchangeRate ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              Current Exchange Rate
            </div>
            <div style={{ 
              color: '#4ade80', 
              fontSize: '3rem', 
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              1 CAD = {exchangeRate.toFixed(4)} USD
            </div>
            <div style={{ color: '#888', fontSize: '0.85rem' }}>
              1 USD = {(1 / exchangeRate).toFixed(4)} CAD
            </div>
            {lastUpdated && (
              <div style={{ 
                color: '#666', 
                fontSize: '0.75rem', 
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #444'
              }}>
                Last updated: {lastUpdated.toLocaleString()}
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#ff6b6b' }}>
            Unable to fetch exchange rate. Please try again later.
          </div>
        )}
      </div>

      {/* Currency Converter */}
      {exchangeRate && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            fontSize: '1.1rem', 
            fontWeight: '600', 
            marginBottom: '1.5rem',
            color: '#333'
          }}>
            🔄 Quick Converter
          </h3>
          
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                🇨🇦 Canadian Dollars (CAD)
              </label>
              <input
                type="number"
                value={cadAmount}
                onChange={(e) => handleCadChange(e.target.value)}
                placeholder="Enter CAD amount"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  fontSize: '1.1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#dc3545'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            
            <div style={{ fontSize: '1.5rem', color: '#999' }}>⇄</div>
            
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                🇺🇸 US Dollars (USD)
              </label>
              <input
                type="number"
                value={usdAmount}
                onChange={(e) => handleUsdChange(e.target.value)}
                placeholder="Enter USD amount"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  fontSize: '1.1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#dc3545'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div style={{
        background: '#f8f9fa',
        borderRadius: '12px',
        padding: '1.5rem',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ 
          fontSize: '1rem', 
          fontWeight: '600', 
          marginBottom: '1rem',
          color: '#333'
        }}>
          ℹ️ Information
        </h3>
        <ul style={{ 
          margin: 0, 
          paddingLeft: '1.25rem',
          color: '#666',
          fontSize: '0.9rem',
          lineHeight: '1.8'
        }}>
          <li>Exchange rates are sourced from the <strong>Bank of Canada</strong></li>
          <li>Rates are cached for 1 hour to reduce API calls</li>
          <li>The rate is also available globally as <code style={{ background: '#e9ecef', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>window.cadToUsdRate</code> for use in calculations</li>
          <li>Click the refresh button below to get the latest rate</li>
        </ul>
        
        <button
          onClick={fetchExchangeRate}
          disabled={loading}
          style={{
            marginTop: '1rem',
            padding: '0.6rem 1.25rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c82333')}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
        >
          {loading ? '⏳ Refreshing...' : '🔄 Refresh Rate'}
        </button>
      </div>
    </div>
  );
};

export default CurrencyExchange;

